import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/common/AppShell';
import { PlaceholderPage } from '@/components/common/PlaceholderPage';
import { guardedLazy } from '@/shared/auth/guardedLazy';
import { RequireAuth } from '@/shared/auth/RequireAuth';

// Public landing
const LandingLayout = guardedLazy(() => import('@/features/landing/LandingLayout'));
const HomePage = guardedLazy(() => import('@/features/landing/pages/HomePage'));
const LandingFeaturesPage = guardedLazy(() => import('@/features/landing/pages/FeaturesPage'));
const HowItWorksPage = guardedLazy(() => import('@/features/landing/pages/HowItWorksPage'));
const LandingCustomersPage = guardedLazy(() => import('@/features/landing/pages/CustomersPage'));
const FaqPage = guardedLazy(() => import('@/features/landing/pages/FaqPage'));
const SolutionsPage = guardedLazy(() => import('@/features/landing/pages/SolutionsPage'));
const PricingPage = guardedLazy(() => import('@/features/landing/pages/PricingPage'));
const PrivacyPage = guardedLazy(() => import('@/features/landing/pages/PrivacyPage'));
const ReturnPolicyPage = guardedLazy(() => import('@/features/landing/pages/ReturnPolicyPage'));
const TermsPage = guardedLazy(() => import('@/features/landing/pages/TermsPage'));
const ContactPage = guardedLazy(() => import('@/features/landing/pages/ContactPage'));

// Auth & system
const LoginPage = guardedLazy(() => import('@/features/auth/pages/LoginPage'));
const NotFound = guardedLazy(() => import('@/features/system/pages/NotFound'));
const Forbidden = guardedLazy(() => import('@/features/system/pages/Forbidden'));

// Main
const RestaurantWorkspace = guardedLazy(() => import('@/features/restaurant/RestaurantWorkspace'));
const Dashboard = guardedLazy(() => import('@/features/dashboard/pages/Dashboard'));
const POSPage = guardedLazy(() => import('@/features/pos/pages/POSPage'));
const CustomerDisplay = guardedLazy(() => import('@/features/customer-display/pages/CustomerDisplay'));

// User Management
const UsersList = guardedLazy(() => import('@/features/users/pages/UsersList'));
const UserForm = guardedLazy(() => import('@/features/users/pages/UserForm'));
const RolesList = guardedLazy(() => import('@/features/roles/pages/RolesList'));
const RoleForm = guardedLazy(() => import('@/features/roles/pages/RoleForm'));
const ProfilePage = guardedLazy(() => import('@/features/profile/pages/ProfilePage'));

// Master data
const CurrenciesList = guardedLazy(() => import('@/features/currencies/pages/CurrenciesList'));
const WarehousesPage = guardedLazy(() => import('@/features/warehouses'));
const CategoriesPage = guardedLazy(() => import('@/features/categories'));
const SubCategoriesPage = guardedLazy(() => import('@/features/subcategories'));
const BrandsPage = guardedLazy(() => import('@/features/brands'));
const UnitsPage = guardedLazy(() => import('@/features/units'));
const PaymentMethodsPage = guardedLazy(() => import('@/features/payment-methods'));

// People
const CustomersPage = guardedLazy(() => import('@/features/customers'));
const CustomerForm = guardedLazy(() => import('@/features/customers/pages/CustomerForm'));
const CustomersImport = guardedLazy(() => import('@/features/customers/pages/CustomersImport'));
const CustomersEcommerce = guardedLazy(() => import('@/features/customers/pages/CustomersEcommerce'));
const CustomersWithoutEcommerce = guardedLazy(
  () => import('@/features/customers/pages/CustomersWithoutEcommerce'),
);
const CustomerLedger = guardedLazy(() => import('@/features/customers/pages/CustomerLedger'));
const CustomerDetails = guardedLazy(() => import('@/features/customers/pages/CustomerDetails'));
const SuppliersPage = guardedLazy(() => import('@/features/suppliers'));
const SuppliersImport = guardedLazy(() => import('@/features/suppliers/pages/SuppliersImport'));

// Products
const ProductsList = guardedLazy(() => import('@/features/products/pages/ProductsList'));
const ProductForm = guardedLazy(() => import('@/features/products/pages/ProductForm'));
const ImportProducts = guardedLazy(() => import('@/features/products/pages/ImportProducts'));
const OpeningStock = guardedLazy(() => import('@/features/products/pages/OpeningStock'));
const PrintLabels = guardedLazy(() => import('@/features/products/pages/PrintLabels'));
const CountStock = guardedLazy(() => import('@/features/products/pages/CountStock'));
const ImportProductsUpdateOnly = guardedLazy(async () => {
  const m = await import('@/features/products/pages/ImportProducts');
  return { default: m.ImportProductsUpdateOnly };
});

// Sales
const SalesList = guardedLazy(() => import('@/features/sales/pages/SalesList'));
const SaleForm = guardedLazy(() => import('@/features/sales/pages/SaleForm'));
const ImportSales = guardedLazy(() => import('@/features/sales/pages/ImportSales'));
const Shipments = guardedLazy(() => import('@/features/sales/pages/Shipments'));

// Purchases
const PurchasesList = guardedLazy(() => import('@/features/purchases/pages/PurchasesList'));
const PurchaseForm = guardedLazy(() => import('@/features/purchases/pages/PurchaseForm'));
const ImportPurchases = guardedLazy(() => import('@/features/purchases/pages/ImportPurchases'));

// Inventory ops
const AdjustmentsPage = guardedLazy(() => import('@/features/inventory/AdjustmentsPage'));
const AdjustmentForm = guardedLazy(() => import('@/features/inventory/AdjustmentForm'));
const DamagesPage = guardedLazy(() => import('@/features/inventory/DamagesPage'));
const DamageForm = guardedLazy(() => import('@/features/inventory/DamageForm'));
const TransfersPage = guardedLazy(() => import('@/features/inventory/TransfersPage'));
const TransferForm = guardedLazy(() => import('@/features/inventory/TransferForm'));

// Quotations & Returns
const QuotationsList = guardedLazy(() => import('@/features/quotations/QuotationsList'));
const QuotationForm = guardedLazy(() => import('@/features/quotations/QuotationForm'));
const SaleReturnsList = guardedLazy(() => import('@/features/sale-returns/SaleReturnsList'));
const PurchaseReturnsList = guardedLazy(() => import('@/features/purchase-returns/PurchaseReturnsList'));

// Accounting
const ExpensesPage = guardedLazy(() => import('@/features/expenses'));
const DepositsPage = guardedLazy(() => import('@/features/deposits'));
const AccountingDashboard = guardedLazy(() => import('@/features/accounting/dashboard/AccountingDashboard'));
const ChartOfAccountsPage = guardedLazy(() => import('@/features/accounting/chart-of-accounts'));
const JournalEntriesPage = guardedLazy(() => import('@/features/accounting/journal-entries'));
const JournalEntryForm = guardedLazy(() => import('@/features/accounting/journal-entries/JournalEntryForm'));
const TransferMoneyPage = guardedLazy(() => import('@/features/accounting/transfer-money'));
const AccountsListPage = guardedLazy(() => import('@/features/accounts'));
const TrialBalancePage = guardedLazy(() => import('@/features/accounting/reports/TrialBalance'));
const BalanceSheetPage = guardedLazy(() => import('@/features/accounting/reports/BalanceSheet'));
const TaxReportV2Page = guardedLazy(() => import('@/features/accounting/reports/TaxReport'));
const ProfitLossV2Page = guardedLazy(() => import('@/features/accounting/reports/ProfitLossV2'));
const ExpenseCategoriesPage = guardedLazy(() => import('@/features/expenses/Categories'));
const DepositCategoriesPage = guardedLazy(() => import('@/features/deposits/Categories'));
const AccountFormPage = guardedLazy(() => import('@/features/accounts/AccountForm'));
const ExpenseFormPage = guardedLazy(() => import('@/features/expenses/ExpenseForm'));
const ExpenseCategoryFormPage = guardedLazy(() => import('@/features/expenses/CategoryForm'));
const DepositFormPage = guardedLazy(() => import('@/features/deposits/DepositForm'));
const DepositCategoryFormPage = guardedLazy(() => import('@/features/deposits/CategoryForm'));
const TransferMoneyFormPage = guardedLazy(() => import('@/features/accounting/transfer-money/TransferMoneyForm'));


// Online Store
const OrdersList = guardedLazy(() => import('@/features/online-store/OrdersList'));
const OrderShow = guardedLazy(() => import('@/features/online-store/OrderShow'));
const StorePagesPage = guardedLazy(() => import('@/features/online-store/PagesList'));
const BannersList = guardedLazy(() => import('@/features/online-store/BannersList'));
const CollectionsList = guardedLazy(() => import('@/features/online-store/CollectionsList'));
const CollectionForm = guardedLazy(() => import('@/features/online-store/CollectionForm'));
const SubscribersList = guardedLazy(() => import('@/features/online-store/SubscribersList'));
const MessagesList = guardedLazy(() => import('@/features/online-store/MessagesList'));
const StoreSettings = guardedLazy(() => import('@/features/online-store/StoreSettings'));

// Integrations
const WooCommercePage = guardedLazy(async () => ({
  default: (await import('@/features/settings/pages/IntegrationsPlaceholder')).WooCommercePage,
}));
const QuickBooksPage = guardedLazy(async () => ({
  default: (await import('@/features/settings/pages/IntegrationsPlaceholder')).QuickBooksPage,
}));

// Reports + Settings hubs
const ReportsIndex = guardedLazy(() => import('@/features/reports/pages/ReportsIndex'));
const SystemSettings = guardedLazy(() => import('@/features/settings/pages/SystemSettings'));
const AppearanceSettings = guardedLazy(() => import('@/features/settings/pages/AppearanceSettings'));
const PosSettings = guardedLazy(() => import('@/features/settings/pages/PosSettings'));
const MailSettings = guardedLazy(() => import('@/features/settings/pages/MailSettings'));
const SmsSettings = guardedLazy(() => import('@/features/settings/pages/SmsSettings'));
const BackupSettings = guardedLazy(() => import('@/features/settings/pages/BackupSettings'));
const ModulesSettings = guardedLazy(() => import('@/features/settings/pages/ModulesSettings'));
const ErrorLogs = guardedLazy(() => import('@/features/settings/pages/ErrorLogs'));
const LoginDevices = guardedLazy(() => import('@/features/settings/pages/LoginDevices'));
const SmsTemplates = guardedLazy(() => import('@/features/settings/pages/SmsTemplates'));
const TranslationsSettings = guardedLazy(() => import('@/features/settings/pages/TranslationsSettings'));
const TranslationsView = guardedLazy(() => import('@/features/settings/pages/TranslationsView'));
const QuickBooksSync = guardedLazy(() => import('@/features/settings/pages/QuickBooksSync'));
const EmailTemplates = guardedLazy(() => import('@/features/settings/pages/EmailTemplates'));
const UpdateSettings = guardedLazy(() => import('@/features/settings/pages/UpdateSettings'));
const PaymentGateway = guardedLazy(() => import('@/features/settings/pages/PaymentGateway'));
const PosReceipt = guardedLazy(() => import('@/features/settings/pages/PosReceipt'));

// Implemented reports (lazy-loaded as a single chunk via the configs file)
const SalesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SalesReport,
}));
const PurchasesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PurchasesReport,
}));
const TopProductsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TopProductsReport,
}));
const TopCustomersReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TopCustomersReport,
}));
const StockReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockReport,
}));
const CustomersReportPage = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CustomersReport,
}));
const SuppliersReportPage = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SuppliersReport,
}));
const ProfitLossReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProfitLossReport,
}));
const QuantityAlertsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).QuantityAlertsReport,
}));
const WarehouseReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).WarehouseReport,
}));
const PaymentsSalesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsSalesReport,
}));
const PaymentsPurchasesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsPurchasesReport,
}));
const PaymentsSaleReturnsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsSaleReturnsReport,
}));
const PaymentsPurchaseReturnsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsPurchaseReturnsReport,
}));

// Long-tail reports
const InventoryValuationReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).InventoryValuationReport,
}));
const DeadStockReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DeadStockReport,
}));
const ZeroSalesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ZeroSalesReport,
}));
const NegativeStockReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).NegativeStockReport,
}));
const StockAgingReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockAgingReport,
}));
const InactiveCustomersReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).InactiveCustomersReport,
}));
const LoyaltyPointsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).LoyaltyPointsReport,
}));
const CashFlowReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CashFlowReport,
}));
const TaxSummaryReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TaxSummaryReport,
}));
const ExpensesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ExpensesReport,
}));
const DepositsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DepositsReport,
}));
const CashRegisterReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CashRegisterReport,
}));
const UsersReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).UsersReport,
}));
const AttendanceReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).AttendanceReport,
}));
const ServiceJobsReportPage = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ServiceJobsReportPage,
}));
const DiscountSummaryReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DiscountSummaryReport,
}));
const ProductSalesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProductSalesReport,
}));
const ProductPurchasesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProductPurchasesReport,
}));
const SellerReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SellerReport,
}));
const ProductReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProductReport,
}));
const TransactionsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TransactionsReport,
}));
const SalesByCategoryReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SalesByCategoryReport,
}));
const SalesByBrandReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SalesByBrandReport,
}));
const DraftInvoicesReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DraftInvoicesReport,
}));
const StockInventoryValuationReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockInventoryValuationReport,
}));
const StockTransferReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockTransferReport,
}));
const StockAdjustmentReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockAdjustmentReport,
}));
const ReturnRatioReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ReturnRatioReport,
}));
const WarrantyGuaranteeReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).WarrantyGuaranteeReport,
}));
const CustomerMaintenanceHistoryReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CustomerMaintenanceHistoryReport,
}));
const ServiceChecklistReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ServiceChecklistReport,
}));
const AnalyticsReport = guardedLazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).AnalyticsReport,
}));
const LoginActivityReport = guardedLazy(() => import('@/features/reports/pages/LoginActivityReport'));
const AIReport = guardedLazy(() => import('@/features/reports/pages/AIReport'));

/**
 * For screens with no API behind them at all. The default placeholder copy
 * promises "the Laravel endpoint already exists", which is not true for these —
 * say so plainly instead of implying the work is nearly done.
 */
const plannedStub = (title: string) => (
  <PlaceholderPage title={title} note={<PlannedNote />} />
);

function PlannedNote() {
  const { t } = useTranslation();
  return (
    <>
      {t(
        'common.planned_description',
        'This feature is on the roadmap — neither the API nor the screen has been built yet.',
      )}
    </>
  );
}

export const router = createBrowserRouter([
  // Public marketing pages (no auth required)
  {
    element: <LandingLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/features', element: <LandingFeaturesPage /> },
      { path: '/how-it-works', element: <HowItWorksPage /> },
      { path: '/customers', element: <LandingCustomersPage /> },
      { path: '/faq', element: <FaqPage /> },
      { path: '/solutions', element: <SolutionsPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/return-policy', element: <ReturnPolicyPage /> },
      { path: '/terms', element: <TermsPage /> },
      { path: '/contact', element: <ContactPage /> },
    ],
  },
  { path: '/login', element: <LoginPage /> },
  { path: '/403', element: <Forbidden /> },

  // Full-screen routes (outside the AppShell)
  {
    element: <RequireAuth />,
    children: [
      { path: '/app/pos', element: <POSPage /> },
      { path: '/app/customer-display', element: <CustomerDisplay /> },
    ],
  },

  // Standard app shell
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/app', element: <Navigate to="/app/dashboard" replace /> },
          { path: '/app/dashboard', element: <Dashboard /> },
          { path: '/app/restaurant', element: <RestaurantWorkspace /> },

          // User Management
          { path: '/app/users', element: <UsersList /> },
          { path: '/app/users/new', element: <UserForm /> },
          { path: '/app/users/:id/edit', element: <UserForm /> },
          { path: '/app/roles', element: <RolesList /> },
          { path: '/app/roles/new', element: <RoleForm /> },
          { path: '/app/roles/:id/edit', element: <RoleForm /> },

          // Products
          { path: '/app/products', element: <ProductsList /> },
          { path: '/app/products/new', element: <ProductForm /> },
          { path: '/app/products/:id/edit', element: <ProductForm /> },
          { path: '/app/products/import', element: <ImportProducts /> },
          { path: '/app/products/import-update', element: <ImportProductsUpdateOnly /> },
          { path: '/app/products/opening-stock', element: <OpeningStock /> },
          { path: '/app/products/print-labels', element: <PrintLabels /> },
          { path: '/app/count-stock', element: <CountStock /> },
          { path: '/app/categories', element: <CategoriesPage /> },
          { path: '/app/subcategories', element: <SubCategoriesPage /> },
          { path: '/app/brands', element: <BrandsPage /> },
          { path: '/app/units', element: <UnitsPage /> },

          // Sales
          { path: '/app/sales', element: <SalesList /> },
          { path: '/app/sales/new', element: <SaleForm /> },
          { path: '/app/sales/:id/edit', element: <SaleForm /> },
          { path: '/app/sales/import', element: <ImportSales /> },
          { path: '/app/quotations', element: <QuotationsList /> },
          { path: '/app/quotations/new', element: <QuotationForm /> },
          { path: '/app/quotations/:id/edit', element: <QuotationForm /> },
          { path: '/app/sale-returns', element: <SaleReturnsList /> },
          { path: '/app/shipments', element: <Shipments /> },

          // Purchases
          { path: '/app/purchases', element: <PurchasesList /> },
          { path: '/app/purchases/new', element: <PurchaseForm /> },
          { path: '/app/purchases/:id/edit', element: <PurchaseForm /> },
          { path: '/app/purchases/import', element: <ImportPurchases /> },
          { path: '/app/purchase-returns', element: <PurchaseReturnsList /> },

          // Inventory
          { path: '/app/adjustments', element: <AdjustmentsPage /> },
          { path: '/app/adjustments/new', element: <AdjustmentForm /> },
          { path: '/app/adjustments/:id/edit', element: <AdjustmentForm /> },
          { path: '/app/damages', element: <DamagesPage /> },
          { path: '/app/damages/new', element: <DamageForm /> },
          { path: '/app/damages/:id/edit', element: <DamageForm /> },
          { path: '/app/transfers', element: <TransfersPage /> },
          { path: '/app/transfers/new', element: <TransferForm /> },
          { path: '/app/transfers/:id/edit', element: <TransferForm /> },
          { path: '/app/warehouses', element: <WarehousesPage /> },

          // People
          { path: '/app/customers', element: <CustomersPage /> },
          { path: '/app/customers/new', element: <CustomerForm /> },
          { path: '/app/customers/:id/edit', element: <CustomerForm /> },
          { path: '/app/customers/:id/ledger', element: <CustomerLedger /> },
          { path: '/app/customers/:id/details', element: <CustomerDetails /> },
          { path: '/app/customers/import', element: <CustomersImport /> },
          { path: '/app/customers/ecommerce', element: <CustomersEcommerce /> },
          {
            path: '/app/customers/without-ecommerce',
            element: <CustomersWithoutEcommerce />,
          },
          { path: '/app/suppliers', element: <SuppliersPage /> },
          { path: '/app/suppliers/import', element: <SuppliersImport /> },

          // Accounting
          { path: '/app/accounting/dashboard', element: <AccountingDashboard /> },
          { path: '/app/accounting/chart-of-accounts', element: <ChartOfAccountsPage /> },
          { path: '/app/accounting/journal-entries', element: <JournalEntriesPage /> },
          { path: '/app/accounting/journal-entries/new', element: <JournalEntryForm /> },
          { path: '/app/accounting/journal-entries/:id/edit', element: <JournalEntryForm /> },
          { path: '/app/accounting/reports/trial-balance', element: <TrialBalancePage /> },
          { path: '/app/accounting/reports/profit-loss', element: <ProfitLossV2Page /> },
          { path: '/app/accounting/reports/balance-sheet', element: <BalanceSheetPage /> },
          { path: '/app/accounting/reports/tax-summary', element: <TaxReportV2Page /> },
          { path: '/app/accounts', element: <AccountsListPage /> },
          { path: '/app/accounts/new', element: <AccountFormPage /> },
          { path: '/app/accounts/:id/edit', element: <AccountFormPage /> },
          { path: '/app/expenses', element: <ExpensesPage /> },
          { path: '/app/expenses/new', element: <ExpenseFormPage /> },
          { path: '/app/expenses/:id/edit', element: <ExpenseFormPage /> },
          { path: '/app/expenses/categories', element: <ExpenseCategoriesPage /> },
          { path: '/app/expenses/categories/new', element: <ExpenseCategoryFormPage /> },
          { path: '/app/expenses/categories/:id/edit', element: <ExpenseCategoryFormPage /> },
          { path: '/app/deposits', element: <DepositsPage /> },
          { path: '/app/deposits/new', element: <DepositFormPage /> },
          { path: '/app/deposits/:id/edit', element: <DepositFormPage /> },
          { path: '/app/deposits/categories', element: <DepositCategoriesPage /> },
          { path: '/app/deposits/categories/new', element: <DepositCategoryFormPage /> },
          { path: '/app/deposits/categories/:id/edit', element: <DepositCategoryFormPage /> },
          { path: '/app/transfer-money', element: <TransferMoneyPage /> },
          { path: '/app/transfer-money/new', element: <TransferMoneyFormPage /> },
          { path: '/app/transfer-money/:id/edit', element: <TransferMoneyFormPage /> },

          // Online Store
          { path: '/app/store/orders', element: <OrdersList /> },
          { path: '/app/store/orders/:id', element: <OrderShow /> },
          { path: '/app/store/pages', element: <StorePagesPage /> },
          { path: '/app/store/banners', element: <BannersList /> },
          { path: '/app/store/collections', element: <CollectionsList /> },
          { path: '/app/store/collections/create', element: <CollectionForm /> },
          { path: '/app/store/collections/:id/edit', element: <CollectionForm /> },
          { path: '/app/store/subscribers', element: <SubscribersList /> },
          { path: '/app/store/messages', element: <MessagesList /> },
          { path: '/app/store/settings', element: <StoreSettings /> },

          // Integrations
          { path: '/app/woocommerce', element: <WooCommercePage /> },
          { path: '/app/quickbooks', element: <QuickBooksPage /> },

          // Reports
          { path: '/app/reports', element: <ReportsIndex /> },
          { path: '/app/reports/sales', element: <SalesReport /> },
          { path: '/app/reports/purchases', element: <PurchasesReport /> },
          { path: '/app/reports/top-selling-products', element: <TopProductsReport /> },
          { path: '/app/reports/top-customers', element: <TopCustomersReport /> },
          { path: '/app/reports/top-suppliers', element: <SuppliersReportPage /> },
          { path: '/app/reports/stock', element: <StockReport /> },
          { path: '/app/reports/customers', element: <CustomersReportPage /> },
          { path: '/app/reports/profit-and-loss', element: <ProfitLossReport /> },
          { path: '/app/reports/quantity-alerts', element: <QuantityAlertsReport /> },
          { path: '/app/reports/warehouse', element: <WarehouseReport /> },
          { path: '/app/reports/payments-sale', element: <PaymentsSalesReport /> },
          { path: '/app/reports/payments-purchase', element: <PaymentsPurchasesReport /> },
          { path: '/app/reports/payments-sales-returns', element: <PaymentsSaleReturnsReport /> },
          { path: '/app/reports/payments-purchase-returns', element: <PaymentsPurchaseReturnsReport /> },
          // Long-tail reports — wired with the same ReportPage framework
          { path: '/app/reports/inventory-valuation', element: <InventoryValuationReport /> },
          { path: '/app/reports/stock-aging', element: <StockAgingReport /> },
          { path: '/app/reports/dead-stock', element: <DeadStockReport /> },
          { path: '/app/reports/zero-sales', element: <ZeroSalesReport /> },
          { path: '/app/reports/negative-stock', element: <NegativeStockReport /> },
          { path: '/app/reports/stock-adjustment', element: <StockAdjustmentReport /> },
          { path: '/app/reports/inactive-customers', element: <InactiveCustomersReport /> },
          { path: '/app/reports/loyalty-points', element: <LoyaltyPointsReport /> },
          { path: '/app/reports/cash-flow', element: <CashFlowReport /> },
          { path: '/app/reports/tax-summary', element: <TaxSummaryReport /> },
          { path: '/app/reports/expenses', element: <ExpensesReport /> },
          { path: '/app/reports/deposits', element: <DepositsReport /> },
          { path: '/app/reports/cash-register', element: <CashRegisterReport /> },
          { path: '/app/reports/discount-summary', element: <DiscountSummaryReport /> },
          { path: '/app/reports/return-ratio', element: <ReturnRatioReport /> },
          { path: '/app/reports/product-purchases', element: <ProductPurchasesReport /> },
          { path: '/app/reports/users', element: <UsersReport /> },
          { path: '/app/reports/login-activity', element: <LoginActivityReport /> },
          { path: '/app/reports/error-logs', element: <ErrorLogs /> },
          { path: '/app/reports/analytics', element: <AnalyticsReport /> },
          { path: '/app/reports/ai', element: <AIReport /> },
          { path: '/app/reports/attendance', element: <AttendanceReport /> },
          { path: '/app/reports/warranty-guarantee', element: <WarrantyGuaranteeReport /> },
          { path: '/app/reports/service-jobs', element: <ServiceJobsReportPage /> },
          { path: '/app/reports/service-checklist', element: <ServiceChecklistReport /> },
          { path: '/app/reports/customer-maintenance-history', element: <CustomerMaintenanceHistoryReport /> },
          // Reports added to match the legacy sidebar (still stubs until backend pages are migrated)
          { path: '/app/reports/product-sales', element: <ProductSalesReport /> },
          { path: '/app/reports/seller', element: <SellerReport /> },
          { path: '/app/reports/draft-invoices', element: <DraftInvoicesReport /> },
          { path: '/app/reports/sales-by-category', element: <SalesByCategoryReport /> },
          { path: '/app/reports/sales-by-brand', element: <SalesByBrandReport /> },
          { path: '/app/reports/suppliers', element: <SuppliersReportPage /> },
          { path: '/app/reports/stock-inventory-valuation', element: <StockInventoryValuationReport /> },
          { path: '/app/reports/stock-transfer', element: <StockTransferReport /> },
          { path: '/app/reports/product', element: <ProductReport /> },
          { path: '/app/reports/transactions', element: <TransactionsReport /> },

          // Settings
          { path: '/app/settings', element: <Navigate to="/app/settings/system" replace /> },
          { path: '/app/settings/system', element: <SystemSettings /> },
          { path: '/app/settings/appearance', element: <AppearanceSettings /> },
          { path: '/app/settings/appearance_settings', element: <AppearanceSettings /> },
          // Languages are managed on the Translations screen — same feature,
          // one implementation.
          { path: '/app/settings/languages', element: <Navigate to="/app/settings/translations" replace /> },
          { path: '/app/settings/translations', element: <TranslationsSettings /> },
          { path: '/app/settings/translations_settings', element: <TranslationsSettings /> },
          { path: '/app/settings/translations/:locale', element: <TranslationsView /> },
          { path: '/app/settings/currencies', element: <CurrenciesList /> },
          // Custom fields and security both ship as tabs of System settings.
          {
            path: '/app/settings/custom-fields',
            element: <Navigate to="/app/settings/system#custom_fields" replace />,
          },
          { path: '/app/settings/pos', element: <PosSettings /> },
          { path: '/app/settings/pos_settings', element: <PosSettings /> },
          { path: '/app/settings/pos-receipt', element: <PosReceipt /> },
          { path: '/app/settings/pos_receipt', element: <PosReceipt /> },
          { path: '/app/settings/payment-methods', element: <PaymentMethodsPage /> },
          { path: '/app/settings/payment_methods', element: <PaymentMethodsPage /> },
          { path: '/app/settings/payment-gateway', element: <PaymentGateway /> },
          { path: '/app/settings/payment_gateway', element: <PaymentGateway /> },
          { path: '/app/settings/mail', element: <MailSettings /> },
          { path: '/app/settings/mail_settings', element: <MailSettings /> },
          { path: '/app/settings/email-templates', element: <EmailTemplates /> },
          { path: '/app/settings/email_templates', element: <EmailTemplates /> },
          { path: '/app/settings/sms', element: <SmsSettings /> },
          { path: '/app/settings/sms_settings', element: <SmsSettings /> },
          { path: '/app/settings/sms-templates', element: <SmsTemplates /> },
          { path: '/app/settings/sms_templates', element: <SmsTemplates /> },
          { path: '/app/settings/quickbooks_sync', element: <QuickBooksSync /> },
          {
            path: '/app/settings/security',
            element: <Navigate to="/app/settings/system#security" replace />,
          },
          { path: '/app/settings/login-devices', element: <LoginDevices /> },
          { path: '/app/settings/login_devices', element: <LoginDevices /> },
          { path: '/app/settings/backup', element: <BackupSettings /> },
          { path: '/app/settings/Backup', element: <BackupSettings /> },
          { path: '/app/settings/Warehouses', element: <WarehousesPage /> },
          { path: '/app/settings/warehouses', element: <WarehousesPage /> },
          { path: '/app/settings/Currencies', element: <CurrenciesList /> },
          { path: '/app/settings/update', element: <UpdateSettings /> },
          { path: '/app/settings/update_settings', element: <UpdateSettings /> },
          { path: '/app/settings/modules', element: <ModulesSettings /> },
          { path: '/app/settings/module_settings', element: <ModulesSettings /> },
          { path: '/app/settings/error-logs', element: <ErrorLogs /> },
          // No API behind these three yet — the placeholder says so honestly.
          { path: '/app/settings/notifications', element: plannedStub('settings.notifications') },
          { path: '/app/settings/google-calendar', element: plannedStub('settings.google_calendar') },
          { path: '/app/settings/api-tokens', element: plannedStub('settings.api_tokens') },

          { path: '/app/profile', element: <ProfilePage /> },

          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
